// ═══════════════════════════════════════════════════════════════════
//  modules/tickets/AttachmentsList.js
//  AttachmentsList
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";

// ═══════════════════════════════════════════════════════════════════════
//  CORRECTIF — Téléchargement des pièces jointes de tickets
//
//  Le problème : dans App.js, le bouton « ⬇ Télécharger » pointait vers
//     /api/attachments/<fichier>?token=<jeton>
//  alors que la route est protégée par requireAuth, qui ne lit QUE l'en-tête
//  « Authorization: Bearer … ». Le ?token= de l'URL n'a jamais été regardé :
//  le serveur répondait 401 et le fichier ne se téléchargeait pas.
//
//  Le correctif : on télécharge comme le fait déjà le module Documents
//  (requête authentifiée → blob → lien temporaire). Aucun jeton ne circule
//  dans une URL, donc rien ne fuite dans l'historique du navigateur ni dans
//  les journaux d'IIS.
//
//  Rien à changer côté serveur.
//
//  ─────────────────────────────────────────────────────────────────────
//  COMMENT L'APPLIQUER
//  Dans C:\inetpub\orialis-frontend-source\src\App.js, remplace la fonction
//  AttachmentsList EN ENTIER (elle commence par « function AttachmentsList »
//  et se termine juste avant « function TicketDetail ») par celle ci-dessous.
//  Puis : npm run build, et déploiement habituel.
// ═══════════════════════════════════════════════════════════════════════
export function AttachmentsList({ ticketId, token }) {
  const [attachments, setAttachments] = useState([]);
  const [telechargement, setTelechargement] = useState(null);

  useEffect(() => {
    const load = async () => {
      const res = await fetch(`${API_BASE}/api/tickets/${ticketId}/attachments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAttachments(Array.isArray(data) ? data : []);
    };
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [ticketId, token]);

  if (attachments.length === 0) return null;

  const getFileIcon = (mimetype) => {
    if (!mimetype) return "📎";
    if (mimetype.startsWith("image/")) return "🖼️";
    if (mimetype.startsWith("video/")) return "🎬";
    if (mimetype.includes("pdf")) return "📄";
    if (mimetype.includes("zip") || mimetype.includes("rar")) return "🗜️";
    return "📎";
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  // Requête authentifiée par en-tête, puis téléchargement du blob.
  // (Un <a href> ne peut pas envoyer l'en-tête Authorization — c'était le bug.)
  const telecharger = async (a) => {
    setTelechargement(a.id);
    try {
      const res = await fetch(`${API_BASE}/api/attachments/${a.filename}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.status === 404 ? "introuvable" : "refus");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = a.original_name || a.filename;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(
        err.message === "introuvable"
          ? "Ce fichier n'est plus sur le serveur (les pièces jointes expirent au bout de 6 mois)."
          : "Téléchargement impossible — ta session a peut-être expiré. Recharge la page et réessaie."
      );
    }
    setTelechargement(null);
  };

  return (
    <div style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
      <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 8 }}>📎 PIÈCES JOINTES ({attachments.length})</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {attachments.map((a) => (
          <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, background: THEME.panel, borderRadius: 6, padding: "8px 12px" }}>
            <span style={{ fontSize: 18 }}>{getFileIcon(a.mimetype)}</span>
            <div style={{ flex: 1, overflow: "hidden" }}>
              <div style={{ color: THEME.text, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.original_name}</div>
              <div style={{ color: THEME.textDim, fontSize: 10 }}>
                {a.uploaded_by && `Envoyé par ${a.uploaded_by} · `}
                {formatSize(a.size)}
                {a.expires_at && ` · Expire le ${new Date(a.expires_at).toLocaleDateString("fr")}`}
              </div>
            </div>
            <button onClick={() => telecharger(a)} disabled={telechargement === a.id}
              style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 5, padding: "5px 10px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", cursor: telechargement === a.id ? "wait" : "pointer", fontFamily: "inherit", opacity: telechargement === a.id ? 0.6 : 1 }}>
              {telechargement === a.id ? "⏳ …" : "⬇ Télécharger"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
